import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RotateCw } from 'lucide-react'
import { api } from '@/lib/api'
import type { AdminJobSummary, Page } from '@/lib/types'
import { PageHeader } from '@/components/AppLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatDate, formatMicros, formatNumber } from '@/lib/utils'
import { ErrorNote } from './DashboardPage'
import { Pagination } from './UsersPage'

export function JobsPage() {
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(0)
  const [inspecting, setInspecting] = useState<AdminJobSummary | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['jobs', status, page],
    queryFn: () =>
      api.get<Page<AdminJobSummary>>('api/admin/generations', {
        status: status || undefined,
        page,
        size: 25,
      }),
    // Queued and running jobs move; the list is worth keeping live.
    refetchInterval: 15_000,
  })

  const retry = useMutation({
    mutationFn: (jobId: string) => api.post(`api/admin/generations/${jobId}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  })

  return (
    <>
      <PageHeader
        title="Generations"
        description="Monitor jobs, inspect the prompt that produced a result, and retry failures."
      />

      <div className="space-y-4 p-8">
        {(error || retry.error) && <ErrorNote message={((error ?? retry.error) as Error).message} />}

        <select
          className="h-9 rounded-lg border bg-[var(--color-surface)] px-3 text-sm"
          value={status}
          onChange={(e) => {
            setPage(0)
            setStatus(e.target.value)
          }}
        >
          <option value="">All statuses</option>
          <option value="QUEUED">Queued</option>
          <option value="RUNNING">Running</option>
          <option value="SUCCEEDED">Succeeded</option>
          <option value="FAILED">Failed</option>
        </select>

        <div className="rounded-[var(--radius-card)] border bg-[var(--color-surface)]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Prompt</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-[var(--color-text-tertiary)]">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {data?.items.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="whitespace-nowrap text-[var(--color-text-secondary)]">
                    {formatDate(job.createdAt)}
                  </TableCell>
                  <TableCell><JobStatusBadge status={job.status} /></TableCell>
                  <TableCell className="text-[var(--color-text-secondary)]">
                    {/* A null owner is a curated generation: made by an
                        administrator for Discover, owned by nobody. */}
                    {job.userId ? <code className="text-xs">{job.userId.slice(0, 8)}</code> : <Badge variant="accent">Curated</Badge>}
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-[var(--color-text-secondary)]">
                    {job.userPrompt ?? '—'}
                  </TableCell>
                  <TableCell className="tabular">{job.attempts}</TableCell>
                  <TableCell className="tabular text-[var(--color-text-secondary)]">
                    {formatMicros(job.costMicros)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setInspecting(job)}>
                        Inspect
                      </Button>
                      {(job.status === 'FAILED' || job.status === 'CANCELLED') && (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={retry.isPending}
                          onClick={() => retry.mutate(job.id)}
                        >
                          <RotateCw className="size-3" />
                          Retry
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Pagination
          page={data?.page ?? 0}
          totalPages={data?.totalPages ?? 0}
          totalItems={data?.totalItems ?? 0}
          onChange={setPage}
        />
      </div>

      <Dialog open={inspecting !== null} onOpenChange={(open) => !open && setInspecting(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generation detail</DialogTitle>
            <DialogDescription>
              The composed prompt is what was actually sent to the model — the place to look when a result is
              wrong.
            </DialogDescription>
          </DialogHeader>
          {inspecting && (
            <div className="space-y-3 text-sm">
              <Field label="Status" value={inspecting.status} />
              <Field label="Provider" value={`${inspecting.provider} · ${inspecting.providerModel ?? '—'}`} />
              <Field label="Credits" value={`${inspecting.creditsCharged}${inspecting.creditsRefunded ? ' (refunded)' : ''}`} />
              <Field label="Cost" value={describeCost(inspecting)} />
              {inspecting.imageSize && (
                <Field label="Billed on" value={`${inspecting.imageSize} · ${inspecting.imageQuality ?? '—'} quality`} />
              )}
              {inspecting.outputTokens != null && (
                <Field
                  label="Tokens"
                  value={`${formatNumber(inspecting.inputTextTokens)} text in · ${formatNumber(inspecting.inputImageTokens)} image in · ${formatNumber(inspecting.outputTokens)} out`}
                />
              )}
              <Field label="User text" value={inspecting.userPrompt ?? '—'} />
              <div>
                <p className="mb-1 text-xs text-[var(--color-text-tertiary)]">Composed prompt</p>
                <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border bg-[var(--color-background)] p-3 text-xs">
                  {inspecting.finalPrompt ?? '—'}
                </pre>
              </div>
              {inspecting.errorDetail && (
                <div>
                  <p className="mb-1 text-xs text-[var(--color-text-tertiary)]">Error</p>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-lg border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-3 text-xs">
                    {inspecting.errorCode}: {inspecting.errorDetail}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

const COST_BASIS_LABEL: Record<NonNullable<AdminJobSummary['costBasis']>, string> = {
  USAGE: 'measured from token usage',
  FLAT_RATE: 'estimated at the flat rate',
  NONE: 'no charge',
}

function describeCost(job: AdminJobSummary): string {
  const cost = formatMicros(job.costMicros, 4)
  return job.costBasis ? `${cost} (${COST_BASIS_LABEL[job.costBasis]})` : cost
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-xs text-[var(--color-text-tertiary)]">{label}</span>
      <span className="text-right text-[var(--color-text-secondary)]">{value}</span>
    </div>
  )
}

export function JobStatusBadge({ status }: { status: AdminJobSummary['status'] }) {
  const variant =
    status === 'SUCCEEDED' ? 'success' : status === 'FAILED' ? 'danger' : status === 'RUNNING' ? 'accent' : 'default'
  return <Badge variant={variant}>{status}</Badge>
}
